import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';
import { mapStatusToGenericInvoiceStatus } from '@app/feature/invoice/helper/invoice-status.helper';
import { mapTypeToGenericInvoiceTypes } from '@app/feature/invoice/helper/invoice-type.helper';

import { useEmspBoInvoiceContext } from '../../emsp-bo-invoice.context';

export const EmspBoInvoiceDetailsInformationCard = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const invoice = useEmspBoInvoiceContext();

  // RENDER ////////////////////////////////////////////////////////////////////

  const { color: statusColor, name: statusName } =
    mapStatusToGenericInvoiceStatus(invoice.status);

  const { color: typeColor, name: typeName } = mapTypeToGenericInvoiceTypes(
    invoice.type,
  );

  return (
    <Card flex={1} gap={GapEnum.M}>
      <AccentTitle
        icon="info-circle"
        as={HeadingEnum.H2}
        text={t('$.emsp-bo-invoice-details.information.title')}
      />
      <Stack justifyContent="space-between">
        <Text bold>{t('delmonicos-shared-sdk.outputs-invoice.type')}</Text>
        <Tag className={classNames(typeColor)}>
          <Text italic fontSize={TextFontSize.Small}>
            {t(`delmonicos-shared-sdk.enums-invoice-type.${typeName}`)}
          </Text>
        </Tag>
      </Stack>
      <Stack justifyContent="space-between">
        <Text bold>{t('delmonicos-shared-sdk.outputs-invoice.status')}</Text>
        <Tag className={classNames(statusColor)}>
          <Text italic fontSize={TextFontSize.Small}>
            {statusName}
          </Text>
        </Tag>
      </Stack>
      <Stack justifyContent="space-between">
        <Text bold>
          {t('delmonicos-shared-sdk.outputs-invoice.invoice-number')}
        </Text>
        <Text>{invoice.invoiceNumber}</Text>
      </Stack>
      <Stack justifyContent="space-between">
        <Text bold>
          {t('delmonicos-shared-sdk.outputs-invoice.period-start')}
        </Text>
        <Text>{formatDate(invoice.periodStart, i18n)}</Text>
      </Stack>
      {invoice.periodEnd && (
        <Stack justifyContent="space-between">
          <Text bold>
            {t('delmonicos-shared-sdk.outputs-invoice.period-end')}
          </Text>
          <Text>{formatDate(invoice.periodEnd, i18n)}</Text>
        </Stack>
      )}
      {invoice.issuedAt && (
        <Stack justifyContent="space-between">
          <Text bold>
            {t('delmonicos-shared-sdk.outputs-invoice.issued-at')}
          </Text>
          <Text>{formatDate(invoice.issuedAt, i18n)}</Text>
        </Stack>
      )}
    </Card>
  );
};
